import React, { useMemo } from "react";
import { Avatar, Box, Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTeamPresenceOptional } from "../context/TeamPresenceContext";
import { presenceDotColor } from "../utils/teamPresence";
import { getInitials } from "../utils/utils";

/**
 * Avatar com bolinha de presença (online / ausente / offline), alinhado ao dashboard Ao vivo.
 */
export default function TeamPresenceAvatar({
  userId,
  src,
  name = "",
  size = 32,
  showDot = true,
  dotSize = 10,
  sx,
  children,
  ...avatarProps
}) {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const presenceCtx = useTeamPresenceOptional();

  const { kind, socketTabs, operator } = useMemo(() => {
    if (!presenceCtx || userId == null) {
      return { kind: null, socketTabs: 0, operator: null };
    }
    return presenceCtx.getPresence(userId);
  }, [presenceCtx, userId]);

  const resolvedSrc = useMemo(() => {
    const explicit = typeof src === "string" ? src.trim() : "";
    if (explicit) return explicit;
    const fromPresence = typeof operator?.imagem === "string" ? operator.imagem.trim() : "";
    return fromPresence || null;
  }, [src, operator?.imagem]);

  const tooltip = useMemo(() => {
    if (!kind) return "";
    const base =
      kind === "online"
        ? t("dashboardPage.live.onlinePresenceTooltip")
        : kind === "away"
          ? t("dashboardPage.live.awayPresenceTooltip")
          : t("dashboardPage.live.offlinePresenceTooltip");
    if (kind === "online" && socketTabs > 0) {
      return `${base}\n${t("dashboardPage.live.openTabsCount", { count: socketTabs })}`;
    }
    return base;
  }, [kind, socketTabs, t]);

  const dotBg = kind ? presenceDotColor(theme, kind) : null;

  return (
    <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0, ...sx }}>
      <Avatar
        src={resolvedSrc || undefined}
        sx={{ width: size, height: size, fontSize: size * 0.38 }}
        {...avatarProps}
      >
        {children ?? (!resolvedSrc ? getInitials(name) : null)}
      </Avatar>
      {showDot && kind ? (
        <Tooltip title={tooltip} placement="right" arrow>
          <Box
            component="span"
            sx={{
              position: "absolute",
              right: -1,
              bottom: -1,
              width: dotSize,
              height: dotSize,
              borderRadius: "50%",
              bgcolor: dotBg,
              border: "2px solid",
              borderColor: "background.paper",
              cursor: "help",
              boxSizing: "border-box",
            }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
}
